import React from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import {CircularProgress} from "@mui/material";
import {palette} from "../../constants";
import {makeStyles} from "tss-react/mui";

interface NavigationArrowProps {
	direction: "left" | "right";
	onClick: (event: React.MouseEvent) => void;
	top: string;
	contentType: string;
	isLoading?: boolean;
	disabled?: boolean;
}

const useStyles = makeStyles<{direction: "left" | "right", top: string}>()((theme, {direction, top}) => ({
	button: {
		position: "absolute" as const,
		zIndex: 10,
		background: palette.orange,
		color: palette.white,
		width: 48,
		height: 48,
		borderRadius: "50%",
		boxShadow: `0px 2px 4px 0px ${palette.black}20, 0px 1px 3px 0px ${palette.black}15`,
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		top,
		...(direction === "left" ? {left: "-30px"} : {right: "-30px"}),
		border: "none",
		outline: "none",
		cursor: "pointer",
		transition: "all 0.2s ease-in-out",
		// Responsive adjustments starting from 1200px
		[theme.breakpoints.down("lg")]: {
			width: 44,
			height: 44,
			...(direction === "left" ? {left: "5px"} : {right: "5px"})
		},
		[theme.breakpoints.down("sm")]: {
			width: 40,
			height: 40,
			...(direction === "left" ? {left: "5px"} : {right: "5px"})
		}
	},
	buttonDisabled: {
		background: `${palette.grey40} !important`,
		border: "none !important",
		cursor: "not-allowed !important",
		"&:hover": {
			boxShadow: `0px 2px 4px 0px ${palette.black}20, 0px 1px 3px 0px ${palette.black}15`
		}
	},
	buttonLoading: {
		cursor: "wait"
	},
	icon: {
		fontSize: 28,
		color: palette.white,
		// Responsive icon size starting from 1200px
		[theme.breakpoints.down("lg")]: {
			fontSize: 26
		},
		[theme.breakpoints.down("sm")]: {
			fontSize: 24
		}
	},
	loadingSpinner: {
		color: palette.white,
		// Responsive spinner size starting from 1200px
		[theme.breakpoints.down("lg")]: {
			width: "22px !important",
			height: "22px !important"
		},
		[theme.breakpoints.down("sm")]: {
			width: "20px !important",
			height: "20px !important"
		}
	}
}));

export const NavigationArrow: React.FC<NavigationArrowProps> = ({
	direction,
	onClick,
	top,
	contentType,
	isLoading = false,
	disabled = false
}) => {
	const {classes} = useStyles({direction, top});

	const handleClick = (event: React.MouseEvent): void => {
		if (disabled || isLoading) {
			event.preventDefault();
			return;
		}
		onClick(event);
	};

	const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>): void => {
		if (disabled || isLoading) return;
		e.currentTarget.style.boxShadow = `0px 4px 8px 0px ${palette.black}30, 0px 2px 6px 0px ${palette.black}25`;
		e.currentTarget.style.cursor = "pointer";
	};

	const handleMouseLeave = (e: React.MouseEvent<HTMLButtonElement>): void => {
		if (disabled || isLoading) return;
		e.currentTarget.style.boxShadow = `0px 2px 4px 0px ${palette.black}20, 0px 1px 3px 0px ${palette.black}15`;
		e.currentTarget.style.cursor = "pointer";
	};

	const buttonClassName = `${classes.button} ${disabled && !isLoading ? classes.buttonDisabled : ""} ${isLoading ? classes.buttonLoading : ""}`;

	return (
		<button
			onClick={handleClick}
			className={buttonClassName}
			aria-label={`Navigate ${direction} in ${contentType}`}
			data-test-id={`${direction}-arrow-${contentType.toLowerCase().replace(/\s+/g, "-")}`}
			disabled={disabled}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
		>
			{isLoading ? (
				<CircularProgress size={24} className={classes.loadingSpinner} />
			) : direction === "left" ? (
				<ArrowBackIcon className={classes.icon} />
			) : (
				<ArrowForwardIcon className={classes.icon} />
			)}
		</button>
	);
};

export default NavigationArrow;